import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { Tooth } from 'lib/types/projectTypes';
import useInput from 'lib/hooks/useInput';
import CustomSpan from '../text/CustomSpan';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import { AppActions } from 'store/actionCreators';
import { overlappingArrayElements } from 'lib/library';

interface MultidieItemProps {
  isListPage: boolean;
  isComplete: boolean;
  isSelected: boolean;
  isEdit: boolean;
  number: number;
  teethIndex: number;
  numberPosition: string;
  tooth: {
    value: Tooth;
    onChange: (e: any) => void;
    setValue: (value: Tooth) => void;
  };
  teeth: {
    value: object[] | any;
    onChange: (e: any) => void;
    setValue: (value: object[] | any) => void;
  };
  copyData?: {
    value: object | null | any;
    onChange: (e: any) => void;
    setValue: (value: object) => void;
  };
  teethContextActions?: {
    value: {
      copy: {
        active: boolean;
        hidden: boolean;
      };
      paste: {
        active: boolean;
        hidden: boolean;
      };
      delete: {
        active: boolean;
        hidden: boolean;
      };
    };
    setValue: (f: (draft: any) => any) => void;
  };
  onToggleTooth?: (value: number) => void;
}

const TeethSvgState = {
  infoToothList: [],
  keyCombine: {
    keyList: new Set(),
    ctrl: false,
    shift: false,
  },
};

const MultidieItem = ({
  isListPage,
  isComplete,
  isSelected,
  isEdit,
  number,
  teethIndex,
  numberPosition,
  tooth,
  teeth,
  copyData,
  teethContextActions,
  onToggleTooth = () => {},
}: MultidieItemProps) => {
  const { t } = useTranslation();
  const [color, setColor] = useState<string>('#c0c7d4');
  const currTooth = useInput({});
  const [exsitTooth, setExsitTooth] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const description = useInput('');

  const hasSelectedData = !!tooth?.value?.hasData;
  const [values, setValues] = useImmer(TeethSvgState);
  const keyCombineList = values.keyCombine.keyList;
  const hasBackspaceKey = keyCombineList.has('BACKSPACE');
  // const hasControlKey = keyCombineList.has('CONTROL');
  const hasDeleteKey = keyCombineList.has('DELETE');
  const hasCtrlKey = keyCombineList.has('CONTROL');
  const teethProps = teeth?.value || [];

  // NOTE: keyboard event
  useEffect(() => {
    if (!isEdit) return;

    handleCombineKey();

    window.addEventListener('keydown', keyDownEventFn);
    window.addEventListener('keyup', keyUpEventFn);

    return () => {
      window.removeEventListener('keydown', keyDownEventFn);
      window.removeEventListener('keyup', keyUpEventFn);
    };
  }, [keyCombineList]);

  const keyDownEventFn = (e: any) => {
    if (e.target.nodeName === 'INPUT' && e.key === 'Backspace') return;
    const key = e.key;
    if (!values.keyCombine.keyList.has(key.toUpperCase())) {
      setValues(draft => {
        draft.keyCombine.keyList.add(key.toUpperCase());
      });
    }
  };

  const keyUpEventFn = (e: any) => {
    if (e.target.nodeName === 'INPUT' && e.key === 'Backspace') return;

    const key = e.key;
    if (values.keyCombine.keyList.has(key.toUpperCase())) {
      setValues(draft => {
        draft.keyCombine.keyList.delete(key.toUpperCase());
      });
    }
  };

  const copyFailPopup = useCallback(() => {
    AppActions.add_popup({
      isOpen: true,
      title: t('MODAL_FAIL_COPY_TITLE'),
      content: t('MODAL_FAIL_COPY_CONTENT'),
      isTitleDefault: true,
      isContentDefault: true,
    });
  }, []);

  const pasteFailPopup = useCallback(() => {
    AppActions.add_popup({
      isOpen: true,
      title: t('MODAL_FAIL_PASTE_TITLE'),
      content: t('MODAL_FAIL_PASTE_CONTENT'),
      isTitleDefault: true,
      isContentDefault: true,
    });
  }, []);

  const handleCombineKey = () => {
    if (!isEdit) return;
    const keyCombineDelete = hasDeleteKey || hasBackspaceKey;
    if (keyCombineDelete) {
      // NOTE: delete
      console.log('delete');
      if (!isSelected) {
        console.log('선택 된 데이타가 없습니다.');
      } else if (isSelected && !hasSelectedData) {
        console.log('선택 됐으나 데이터가 없습니다.');
      } else {
        teeth.setValue((draft: Tooth[]) => draft.filter(i => i.number !== tooth.value.number));
        const emptyData = { number: null, hasData: false };
        tooth.setValue(emptyData);
        if (tooth.value.number === copyData?.value.number) {
          copyData?.setValue(emptyData);
          teethContextActions?.setValue(draft => {
            draft.paste.active = false;
          });
        }
        teethContextActions?.setValue(draft => {
          draft.copy.active = false;
          draft.delete.active = false;
        });
      }
    }
  };

  useEffect(() => {
    if (teeth.value < 1) {
      currTooth.setValue({ number: number });
    }

    const findTooth: Tooth = teeth.value?.find(
      (item: Tooth) => Number(item.number) === Number(number),
    );
    setDisabled(!isEdit || !findTooth);
    if (!!findTooth) {
      setExsitTooth(true);
      currTooth.setValue({ ...findTooth, hasData: true });
      description.setValue(findTooth.description);
      setColor(findTooth.color || '#c0c7d4');
    } else {
      setExsitTooth(false);
      currTooth.setValue({ number: number, hasData: false });
      setColor('#c0c7d4');
      description.setValue('');
    }
  }, [teeth.value]);

  const handleClickDieDescription = () => {
    tooth.setValue(currTooth.value);
  };

  const handleChangeDieDescription = (e: any, number: number) => {
    if (e.target.value.length > 50) return;
    description.onChange(e);
    tooth.setValue({ ...tooth.value, description: e.target.value });
    teeth.setValue((draft: Tooth[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.number === number ? { ...curr, description: e.target.value } : curr);
      }, [] as Tooth[]);
    });
  };

  const handleFocusTooth = (e: any) => {
    tooth.setValue({ ...tooth.value, description: e.target.value });
    teeth.setValue((draft: Tooth[]) => {
      return draft.reduce((acc, curr) => {
        return acc.concat(curr.number === number ? { ...curr, description: e.target.value } : curr);
      }, [] as Tooth[]);
    });
  };

  const handleClick = (e: any, number: number) => {
    const target = e.target;
    const isRightClick = e.type === 'contextmenu';
    if (isEdit && !isRightClick) {
      if (!!navigator.userAgent.match(/Macintosh/i)) {
        console.log('TEST] userAgent Macintosh click _____');
        tooth.setValue(currTooth.value);
        onToggleTooth(number);
        return;
      }
      console.log('TEST] userAgent Window click _____');
      if (hasCtrlKey) {
        const currentToothData = tooth.value;
        if (tooth.value.number === number) return console.log('ctrl 누르고 같은 치아 클릭 ');

        if (!currentToothData?.number || !currentToothData?.hasData) {
          setValues(draft => {
            draft.keyCombine.keyList = new Set([]);
          });
          pasteFailPopup();
          return console.log('카피된 데이터 없음');
        }

        const pasteToothData = {
          ...currentToothData,
          number: number,
        };
        teeth.setValue((draft: Tooth[]) =>
          overlappingArrayElements({ list: draft, value: pasteToothData, condition: 'number' }),
        );
        tooth.setValue({ ...tooth.value, number: number });
      } else {
        tooth.setValue(currTooth.value);
        onToggleTooth(number);
        return;
      }
    }
    if (isEdit && isRightClick) {
      if (hasCtrlKey) {
        if (!navigator.userAgent.match(/Macintosh/i)) return;
        // Macintosh인 경우에
        const currentToothData = tooth.value;
        if (tooth.value.number === number) return console.log('ctrl 누르고 같은 치아 클릭 ');

        if (!currentToothData?.number || !currentToothData?.hasData) {
          setValues(draft => {
            draft.keyCombine.keyList = new Set([]);
          });
          pasteFailPopup();
          return console.log('카피된 데이터 없음');
        }

        const pasteToothData = {
          ...currentToothData,
          number: number,
        };
        teeth.setValue((draft: Tooth[]) =>
          overlappingArrayElements({ list: draft, value: pasteToothData, condition: 'number' }),
        );
        tooth.setValue({ ...tooth.value, number: number });
      } else {
        const currentToothData = teethProps.find((item: any) => item.number === number);
        tooth.setValue(currTooth.value);
        teethContextActions?.setValue((draft: any) => {
          draft.paste.active = copyData?.value?.hasData;

          if (currentToothData) {
            draft.copy.active = true;
            draft.delete.active = true;
          } else {
            draft.copy.active = false;
            draft.delete.active = false;
          }
        });
      }
    }
    if (!isEdit && !isRightClick) {
      if (tooth.value.number === number) {
        tooth.setValue({ number: null });
      } else {
        tooth.setValue(currTooth.value);
      }
    }
  };

  return (
    <StyledMultidieItem
      data-component-name="MultidieItem"
      className={cx('multidieItem__container', {
        isHover: !(!isEdit && !exsitTooth),
      })}
      dieColor={color}
    >
      <div className="multidieItem__box">
        <div
          className={cx('multidieItem__icon', {
            isSelected: !!isSelected,
            isListPage: isListPage,
            isComplete: isComplete,
          })}
          onClick={(e: any) => {
            if (!isEdit && !exsitTooth) return;
            handleClick(e, number);
          }}
          onContextMenu={e => {
            handleClick(e, number);
          }}
        ></div>
        {!isComplete && !isListPage && (
          <div
            className={cx('multidieItem__description_box', {
              disabled: !!isEdit && disabled,
            })}
          >
            <input
              className={cx('multidieItem__description')}
              type="text"
              disabled={disabled}
              value={description.value}
              onKeyDown={keyDownEventFn}
              onKeyUp={keyUpEventFn}
              onChange={e => {
                handleChangeDieDescription(e, number);
              }}
              onClick={() => {
                handleClickDieDescription();
              }}
              onFocus={e => {
                console.log('HDS] FOCUS');
                handleFocusTooth(e);
              }}
            />
            {disabled && (
              <div
                className="multidieItem__description_dim"
                onClick={() => {
                  handleClickDieDescription();
                }}
              ></div>
            )}
          </div>
        )}

        {!!isComplete && (
          <div className={cx('multidieItem__description_box isComplete', {})}>
            <span>{description.value}</span>
          </div>
        )}

        {!isListPage && exsitTooth && (
          <CustomSpan
            className={cx(`multidieItem__indication`, { isComplete: isComplete })}
            fontSize={!!isComplete ? 10 : 12}
            fontWeight={500}
            fontColor="#fff"
            onClick={() => {
              handleClickDieDescription();
            }}
          >
            {t(`INDICATION_${currTooth.value?.indicationAlias}`)}
          </CustomSpan>
        )}

        {!isListPage && (
          <CustomSpan
            className={`multidieItem__index ${numberPosition}`}
            fontSize={!!isComplete ? 14 : 18}
            fontWeight={500}
          >
            {teethIndex}
          </CustomSpan>
        )}
      </div>
    </StyledMultidieItem>
  );
};

const StyledMultidieItem = styled.div<{
  dieColor: string;
}>`
  &.isHover {
    &:hover {
      cursor: pointer;
    }
  }
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  .multidieItem__box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    .multidieItem__description_box {
      position: absolute;
      bottom: 20px;
      border-radius: 2px;
      overflow: hidden;
      padding: 0;
      background-color: #fff;
      &.disabled {
        background-color: transparent;
      }
      &.isComplete {
        bottom: 18px;
        height: 15px;
        width: 100px;
        display: flex;
        align-items: center;
        font-size: 10px;
      }
      .multidieItem__description {
        height: 20px;
        width: 130px;
        border: none;
      }
      .multidieItem__description_dim {
        position: absolute;
        top: 0;
        left: 0;
        height: 20px;
        width: 130px;
        background-color: transparent;
      }
    }
    .multidieItem__indication {
      width: 100%;
      text-align: center;
      position: absolute;
      top: 82px;
      &.isComplete {
        top: 54px;
      }
    }
    .multidieItem__index {
      position: absolute;
      color: white;
      &.die1 {
        top: -7px;
        left: 50%;
        transform: translate(-50%, -100%);
      }
      &.die2 {
        top: -5px;
        right: -15px;
      }
      &.die3 {
        bottom: -5px;
        right: -15px;
      }
      &.die4,
      &.die7 {
        bottom: -7px;
        left: 50%;
        transform: translate(-50%, 100%);
      }
      &.die5 {
        bottom: -5px;
        left: -15px;
      }
      &.die6 {
        top: -5px;
        left: -15px;
      }
    }
    .multidieItem__icon {
      width: 96px;
      height: 96px;
      background-color: white;
      border-radius: 50%;
      background-color: ${({ dieColor }) => dieColor};
      &.isSelected {
        border: 2px solid red;
      }
      &.isListPage {
        width: 30px;
        height: 30px;
      }
      &.isComplete {
        width: 72px;
        height: 72px;
      }
    }
  }
`;

export default React.memo(MultidieItem);
